/**
 * Token 使用明细弹窗：按日期、供应商、模型、用户 ID 筛选，分页查询。
 * 日期和供应商改动后立即查询；模型和用户 ID 按回车或点「查询」后生效（与旧版一致）。
 */
import { useState } from 'react'
import { Button, Col, DatePicker, Divider, Input, InputNumber, Modal, Row, Select, Space, Table, Tag, Typography, type TableColumnsType } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import type { UsageDetailItem, UsageDetailsParams } from '@/api/tokenUsage'
import { formatDateTime } from '@/utils/datetime'
import { detailProviderColor } from './constants'
import { useUsageDetails } from './useTokenStats'
import styles from './TokenUsagePage.module.css'

interface DetailFilters {
  dateRange: [Dayjs, Dayjs] | null
  provider: string
  model: string
  userId: number | null
}

// 系统请求的 user_id 是 -1，测试请求是 0
// 明细筛选的列宽：日期范围放得下两个完整日期，其余三项平分剩余宽度
const DATE_COLUMN_FLEX = '0 0 300px'
const FILTER_COLUMN_FLEX = '1 1 180px'

function UserCell({ userId }: Readonly<{ userId: number | string }>) {
  if (userId === -1 || userId === '系统') return <Tag color="orange">系统</Tag>
  if (userId === 0 || userId === '测试') return <Tag color="orange">测试</Tag>
  return <span>{userId}</span>
}

const columns: TableColumnsType<UsageDetailItem> = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 80,
    render: (id: number) => (
      <Typography.Text type="secondary" className={styles.small}>
        #{id}
      </Typography.Text>
    )
  },
  {
    title: '时间',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 140,
    render: (value: string) => (
      <Typography.Text type="secondary" className={styles.small}>
        {formatDateTime(value)}
      </Typography.Text>
    )
  },
  { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 100, render: (id: number) => <UserCell userId={id} /> },
  {
    title: '供应商',
    dataIndex: 'provider',
    key: 'provider',
    width: 100,
    render: (provider: string) => <Tag color={detailProviderColor(provider)}>{provider.toUpperCase()}</Tag>
  },
  {
    title: '模型',
    dataIndex: 'model',
    key: 'model',
    width: 200,
    render: (model: string) => (
      <Typography.Text code className={styles.small}>
        {model}
      </Typography.Text>
    )
  },
  {
    title: 'Token使用',
    key: 'tokens',
    width: 200,
    render: (_, record) => (
      <div className={styles.small}>
        <Typography.Text type="secondary">输入: {record.prompt_tokens}</Typography.Text>
        <Divider orientation="vertical" />
        <Typography.Text type="secondary">输出: {record.completion_tokens}</Typography.Text>
        <Divider orientation="vertical" />
        <strong>总计: {record.total_tokens}</strong>
      </div>
    )
  },
  {
    title: '成本',
    dataIndex: 'total_cost',
    key: 'cost',
    width: 100,
    render: (cost: number) => (
      <strong>
        <Typography.Text type="danger">${(cost ?? 0).toFixed(6)}</Typography.Text>
      </strong>
    )
  },
  { title: '用途', dataIndex: 'purpose', key: 'purpose', width: 120 },
  {
    title: '请求ID',
    dataIndex: 'request_id',
    key: 'request_id',
    width: 100,
    render: (requestId: string | null) => (
      <Typography.Text type="secondary" className={styles.tiny}>
        {requestId ? `${requestId.substring(0, 8)}...` : '-'}
      </Typography.Text>
    )
  }
]

// 筛选条件转成接口参数；空值不传
export function toDetailParams(filters: DetailFilters, page: number, pageSize: number): UsageDetailsParams {
  const params: UsageDetailsParams = { page, page_size: pageSize }
  if (filters.dateRange) {
    params.start_date = filters.dateRange[0].format('YYYY-MM-DD')
    params.end_date = filters.dateRange[1].format('YYYY-MM-DD')
  }
  if (filters.provider) params.provider = filters.provider
  if (filters.model) params.model = filters.model
  if (filters.userId) params.user_id = filters.userId
  return params
}

interface TokenUsageDetailsModalProps {
  open: boolean
  // 第一次打开时的日期范围（取页面当前的统计范围）
  initialDateRange: [Dayjs, Dayjs]
  providerOptions: string[]
  onClose: () => void
}

export function TokenUsageDetailsModal({
  open,
  initialDateRange,
  providerOptions,
  onClose
}: Readonly<TokenUsageDetailsModalProps>) {
  const initialFilters: DetailFilters = { dateRange: initialDateRange, provider: '', model: '', userId: null }
  const [filters, setFilters] = useState<DetailFilters>(initialFilters)
  const [applied, setApplied] = useState<DetailFilters>(initialFilters)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const { data, isFetching } = useUsageDetails(toDetailParams(applied, page, pageSize), open)

  // 立即生效的筛选（日期、供应商）：同时更新输入值和查询条件，回到第一页
  const applyNow = (patch: Partial<DetailFilters>) => {
    setFilters((current) => ({ ...current, ...patch }))
    setApplied((current) => ({ ...current, ...patch }))
    setPage(1)
  }

  const search = () => {
    setApplied(filters)
    setPage(1)
  }

  const reset = () => {
    const resetFilters: DetailFilters = { dateRange: initialDateRange, provider: '', model: '', userId: null }
    setFilters(resetFilters)
    setApplied(resetFilters)
    setPage(1)
  }

  const total = data?.total ?? 0

  return (
    <Modal open={open} title={`Token使用明细 (${total}条记录)`} width="calc(90% - 180px)" footer={null} onCancel={onClose}>
      <div className={styles.detailFilters}>
        {/* 日期范围固定宽度，否则四列平分时两个日期显示不全；窄屏时其余筛选项换行 */}
        <Row gutter={[16, 12]}>
          <Col flex={DATE_COLUMN_FLEX}>
            <div className={styles.filterLabel}>日期范围 :</div>
            <DatePicker.RangePicker
              aria-label="明细日期范围"
              className={styles.fullWidth}
              value={filters.dateRange}
              onChange={(dates) => applyNow({ dateRange: dates?.[0] && dates[1] ? [dates[0], dates[1]] : null })}
            />
          </Col>
          <Col flex={FILTER_COLUMN_FLEX}>
            <div className={styles.filterLabel}>供应商 :</div>
            <Select
              aria-label="供应商"
              className={styles.fullWidth}
              placeholder="全部供应商"
              allowClear
              value={filters.provider}
              options={[
                { value: '', label: '全部供应商' },
                ...providerOptions.map((provider) => ({ value: provider, label: provider.toUpperCase() }))
              ]}
              onChange={(value) => applyNow({ provider: value ?? '' })}
            />
          </Col>
          <Col flex={FILTER_COLUMN_FLEX}>
            <div className={styles.filterLabel}>模型 :</div>
            <Input
              placeholder="输入模型名称"
              value={filters.model}
              onChange={(event) => setFilters((current) => ({ ...current, model: event.target.value }))}
              onPressEnter={search}
            />
          </Col>
          <Col flex={FILTER_COLUMN_FLEX}>
            <div className={styles.filterLabel}>用户ID :</div>
            <InputNumber
              className={styles.fullWidth}
              placeholder="用户ID"
              value={filters.userId}
              onChange={(value) => setFilters((current) => ({ ...current, userId: value }))}
              onPressEnter={search}
            />
          </Col>
        </Row>
        <div className={styles.filterActions}>
          <Space>
            <Button type="primary" icon={<SearchOutlined />} loading={isFetching} onClick={search}>
              查询
            </Button>
            <Button onClick={reset}>重置</Button>
          </Space>
        </div>
      </div>

      <Table<UsageDetailItem>
        rowKey="id"
        columns={columns}
        dataSource={data?.items}
        loading={isFetching}
        size="small"
        scroll={{ x: 1200 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (count) => `共 ${count} 条记录`
        }}
        onChange={(pagination) => {
          setPage(pagination.current ?? 1)
          setPageSize(pagination.pageSize ?? 20)
        }}
      />
    </Modal>
  )
}
